import { CSSProperties, useState } from "react"
import { useDispatch } from "react-redux"
import { Button, Card } from "reactstrap"

import { ISupportRequest, SupportRequestState } from "@api/schema"
import ConfirmModal from "@components/common/ConfirmModal"
import Icon from "@components/common/Icon"
import { withDynamicNamespaces } from "@components/hoc/withDynamicNamespaces"
import { addNotificationAction } from "@redux/actions/notifications"
import { IFormikActions, deleteModelAction, updateModelAction } from "@redux/helper/actions"
import { EntityType } from "@redux/reduxTypes"
import { NamespaceShortcut, prefixedKey, useDynamicTranslation } from "@services/i18n"

const usedNamespaces: NamespaceShortcut[] = ["supportRequest"]

interface IProps {
  /**
   * should elements like buttons be accessible by keyboard? default: true.
   * Set to false, if the card is not at the front of a flipping element (@see: FlippingElement.tsx)
   */
  accessibleByKeyboard?: boolean
  style: CSSProperties
  className: string
  setUpdateModeIsActive: (updating: boolean) => void
  supportRequest: ISupportRequest
}

/**
 * to flip the card it is necessary to define a front and a back card
 * this is the back card
 *
 * Provide action buttons to delete, close or hide a SupportRequest which is only used by the SupportRequestCard.
 * These action buttons are extracted into this extra component from the SupportRequestCard
 * to prevent a reappearing of the modal, after the submit button is clicked.
 * This is caused by a rerendering of the SupportRequestCard when the prop SupportRequest is changed.
 */
const SupportRequestBackCard: React.FC<IProps> = (props: IProps) => {
  const t = useDynamicTranslation()
  const {
    accessibleByKeyboard = true,
    style,
    className,
    setUpdateModeIsActive,
    supportRequest,
  } = props
  const dispatch = useDispatch()

  const [closeModalOpen, setCloseModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [hideModalOpen, setHideModalOpen] = useState(false)

  const onDelete = () => {
    const onSuccess = () => {
      dispatch(addNotificationAction(prefixedKey("partner-market", "supportRequest.deleted")))
    }

    dispatch(deleteModelAction(
      EntityType.SupportRequest,
      supportRequest,
      { onSuccess } as IFormikActions,
    ))
  }

  const onHide = () => {
    const onSuccess = () => {
      dispatch(addNotificationAction(prefixedKey("partner-market", "supportRequest.hidden")))
    }

    const updatedSupportRequest: ISupportRequest = {
      "@id": supportRequest['@id'],
      state: SupportRequestState.Hidden
    }

    dispatch(updateModelAction(EntityType.SupportRequest, updatedSupportRequest, { onSuccess } as IFormikActions))
  }

  const onClose = () => {
    const onSuccess = () => {
      dispatch(addNotificationAction(prefixedKey("partner-market", "supportRequest.closed")))
    }

    const updatedSupportRequest: ISupportRequest = {
      "@id": supportRequest['@id'],
      state: SupportRequestState.Closed
    }

    dispatch(updateModelAction(EntityType.SupportRequest, updatedSupportRequest, { onSuccess } as IFormikActions))
  }

  const onEnterOnUpdateButton = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") {
      setUpdateModeIsActive(true)
    }
  }

  return <Card
    className={className}
    style={style}
  >
    <div className="support-request-card__back__body">
      <h5 className="text-center">{t("supportRequest", "publicUntil.header")}</h5>
      <p className="text-center">
        {supportRequest.publicUntil
          ? t("common", "default.dateTime", { value: supportRequest.publicUntil })
          : t("supportRequest", "state.unpublished")
        }
        &nbsp;
        <Icon
          name="calendar"
          title={t("partner-market", "supportRequest.updatePublishedUntil")}
          size={24}
        />
      </p>
      <div className="btn-flex-layout align-center">
        <Button
          onClick={() => setUpdateModeIsActive(true)}
          onKeyDown={onEnterOnUpdateButton}
          // do not allow access by keyboard, if the component's elements should not be accessible:
          // needed for flipping elements
          tabIndex={accessibleByKeyboard ? 0 : -1}
        >
          <Icon name="pencil" size={24} />{t("partner-market", "supportRequest.update")}
        </Button>
        <Button
          onClick={() => setDeleteModalOpen(true)}
          // do not allow access by keyboard, if the component's elements should not be accessible:
          // needed for flipping elements
          tabIndex={accessibleByKeyboard ? 0 : -1}
        >
          <Icon name="trash" size={24} />{t("partner-market", "supportRequest.delete")}
        </Button>
        <Button
          disabled={supportRequest.state !== SupportRequestState.Public}
          onClick={() => setHideModalOpen(true)}
          // do not allow access by keyboard, if the component's elements should not be accessible:
          // needed for flipping elements
          tabIndex={accessibleByKeyboard ? 0 : -1}
        >
          {t("partner-market", "supportRequest.hide")}
        </Button>
        <Button
          disabled={supportRequest.state === SupportRequestState.Closed}
          onClick={() => setCloseModalOpen(true)}
          // do not allow access by keyboard, if the component's elements should not be accessible:
          // needed for flipping elements
          tabIndex={accessibleByKeyboard ? 0 : -1}
        >
          {t("partner-market", "supportRequest.close")}
        </Button>
        <ConfirmModal
          title={t("partner-market", "supportRequest.delete")}
          content={t("partner-market", "supportRequest.deleteConfirm")}
          onSubmit={onDelete}
          submitButtonLabel={t("partner-market", "supportRequest.delete")}
          modalOpen={deleteModalOpen}
          toggle={() => setDeleteModalOpen(false)}
        />
        <ConfirmModal
          title={t("partner-market", "supportRequest.hide")}
          content={t("partner-market", "supportRequest.hideConfirm")}
          onSubmit={onHide}
          submitButtonLabel={t("partner-market", "supportRequest.hide")}
          modalOpen={hideModalOpen}
          toggle={() => setHideModalOpen(false)}
        />
        <ConfirmModal
          title={t("partner-market", "supportRequest.close")}
          content={t("partner-market", "supportRequest.closeConfirm")}
          onSubmit={onClose}
          submitButtonLabel={t("partner-market", "supportRequest.close")}
          modalOpen={closeModalOpen}
          toggle={() => setCloseModalOpen(false)}
        />
      </div>
    </div>
  </Card >
}

export default withDynamicNamespaces<IProps>(SupportRequestBackCard, usedNamespaces)